import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import classnames from 'classnames';
import { DataLayer } from '@grandvisionhq/analytics';
import { LocationSearch } from '@grandvisionhq/locations';
import { useIntl } from '@grandvisionhq/state';
import StoreFinderMapContainer from '../StoreFinderMap/StoreFinderMapContainer.js';
import StoreListTopContent from '../StoreListTopContent/StoreListTopContent.js';
import { StoreSearchFilters } from '../StoreSearchFilters/StoreSearchFilters.js';
import { useStoreFinderContext } from '../../context/StoreFinder/context.js';
import '@grandvisionhq/utils';
import 'lodash/deburr';
import '../../context/StoreFinderSlots/context.js';
import { storesSearchFilterEvent } from '../../data-layer/actions.js';
import useStoreSearchFilters from '../../hooks/use-store-search-filters.js';
import { useTabs } from '../../hooks/use-tabs.js';
import { StoreFinderFilterWarning } from '../StoreFinderFilterWarning/StoreFinderFilterWarning.js';
import StoreFinderItemContainer from '../StoreFinderItem/StoreFinderItemContainer.js';

/**
 * I've chosen to create a StoreGroupFinderContainer instead of re-using the default StoreFinderContainer .
 * Ideally we would recreate a separate StoreFinderContainer for each use case.
 * This way we should have more control over the implementation.
 * We are actually already doing this, but the StoreFinderContainer is now becoming a mess
 * because it's reused for different purposes.
 */ const StoreGroupFinderContainer = ({ className , storeSearchFilters: propStoreSearchFilters , groupName , urls ,  })=>{
    const { getFormattedLabel  } = useIntl();
    const { isStoresLoading , stores , config , setHoveredStore  } = useStoreFinderContext();
    const [{ setFilterActivity , applyActiveFilters , clearAllFilters  }, { storeSearchFilters , storeSearchFilterList , hasActiveFilters , loading , activeFilters  }, ] = useStoreSearchFilters({
        filters: propStoreSearchFilters
    });
    const { googleMapsId  } = config;
    const prevActiveFilters = React.useRef([]);
    const showTabs = useTabs();
    /**
   *  Should be refactored and it shouldn't rely on state
   *  We should also utilize the Google Maps API to hover the Pin when hovering the list
   *
   *  Part of future refactor
   *  Story: GAAB-5320
   *  https://jira.grandvision.global/browse/GAAB-5320
   */ const onMouseEnterHandler = React.useCallback((store)=>setHoveredStore(store)
    , [
        setHoveredStore
    ]);
    const onMouseLeaveHandler = React.useCallback(()=>setHoveredStore(null)
    , [
        setHoveredStore
    ]);
    const filteredStores = React.useMemo(()=>applyActiveFilters(stores)
    , [
        stores,
        applyActiveFilters
    ]);
    /**
   * I don't want to have this logic in this place, as it also lives in StoreFinderContainer
   * If we need to use it in both places, consider a better place
   * OR if there really is no other option: Convert it into a hook so it's easily re-usable.
   */ React.useEffect(()=>{
        if (prevActiveFilters.current.length !== activeFilters.length) {
            prevActiveFilters.current = activeFilters;
            const dataLayerEvent = storesSearchFilterEvent({
                activeFilters,
                searchResults: filteredStores.length
            });
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        activeFilters,
        filteredStores.length
    ]);
    return /*#__PURE__*/ React.createElement(LocationSearch, {
        className: classnames('store-group-finder', className),
        googleMapsId: googleMapsId,
        showTabs: showTabs,
        searchForm: stores.length ? /*#__PURE__*/ React.createElement(StoreSearchFilters, {
            filters: storeSearchFilterList,
            setFilterActivity: setFilterActivity,
            className: "store-group-finder__search-filters"
        }) : null,
        listTopContent: /*#__PURE__*/ React.createElement(StoreListTopContent, {
            className: "store-group-finder__list-top-content",
            title: getFormattedLabel('stores.page.group.storeFinder.title', groupName ? {
                labelData: {
                    groupName
                }
            } : undefined),
            description: getFormattedLabel('stores.page.group.storeFinder.description', {
                hideLabelKey: true,
                labelData: {
                    storeCount: stores.length
                }
            })
        }),
        listInfoContent: /*#__PURE__*/ React.createElement(StoreFinderFilterWarning, {
            applyActiveFilters: applyActiveFilters,
            clearAllFilters: clearAllFilters,
            hasActiveFilters: hasActiveFilters
        }),
        loadingListItems: isStoresLoading,
        listItems: filteredStores.map((store)=>/*#__PURE__*/ React.createElement(StoreFinderItemContainer, {
                loading: loading,
                key: store.globalStoreId,
                store: store,
                onMouseEnter: onMouseEnterHandler,
                onMouseLeave: onMouseLeaveHandler,
                urls: urls,
                showAttributesAsTags: true
            })
        ),
        mapContent: /*#__PURE__*/ React.createElement(StoreFinderMapContainer, {
            stores: filteredStores,
            infoWindowContentComponent: (props)=>/*#__PURE__*/ React.createElement(StoreFinderItemContainer, swcHelpers.extends({}, props, {
                    urls: urls,
                    view: "map",
                    showAttributesAsTags: true
                }))
        })
    });
};

export { StoreGroupFinderContainer as default };
//# sourceMappingURL=StoreGroupFinderContainer.js.map
